"use client";

import type { ReactNode } from "react";

import { clsx } from "clsx";

import { LimitReachedOverlay, type TrackingConfig } from "./LimitReachedOverlay";

export interface LimitReachedConfig {
  title?: string;
  description?: string;
  buttonText?: string;
  ctaLink?: string;
  variant?: "default" | "redesigned";
  trackingConfig?: TrackingConfig;
}

export interface OmniboxLayoutWrapperProps {
  children: ReactNode;
  className?: string;

  /** Height classes for responsive sizing. Default: "tw-h-[21.25rem] md:tw-h-[36rem]" */
  height?: string;
  /** Padding classes for responsive sizing. Default: "tw-p-4 md:tw-p-8" */
  padding?: string;
  /** Background color class. Default: "tw-bg-cream" */
  backgroundColor?: string;
  /** Border color class. Default: "tw-border-gray-200" */
  borderColor?: string;
  /** Border radius class. Default: "tw-rounded-3xl" */
  borderRadius?: string;

  /** Whether to hide section side lines. Default: true */
  hideSideLines?: boolean;

  /** Whether to show the limit reached overlay (controlled) */
  showLimitReachedOverlay?: boolean;
  /** Callback when limit reached overlay is closed */
  onCloseLimitReachedOverlay?: () => void;
  /** Configuration for limit reached overlay */
  limitReachedConfig?: LimitReachedConfig;

  /** Optional footer rendered below the content container */
  footer?: ReactNode;
  /** Whether the footer is visually shown. Controls bottom border-radius on the content container. */
  showFooter?: boolean;
}

/**
 * Reusable wrapper for omnibox demo components.
 * Handles side-line hiding, structural styling, and limit reached overlay.
 *
 * Used by VoiceIsolatorV2, VoiceChangerV2, VoiceCloningV2, and OmniboxDeveloper.
 */
export function OmniboxLayoutWrapper({
  children,
  className,
  height = "tw-h-[21.25rem] md:tw-h-[36rem]",
  padding = "tw-p-4 md:tw-p-8",
  backgroundColor = "tw-bg-cream",
  borderColor = "tw-border-gray-200",
  borderRadius = "tw-rounded-3xl",
  hideSideLines = true,
  showLimitReachedOverlay = false,
  onCloseLimitReachedOverlay,
  limitReachedConfig,
  footer,
}: OmniboxLayoutWrapperProps) {
  return (
    <div className='tw-relative tw-isolate tw-w-full'>
      {/* Overlay to hide the section side lines */}
      {hideSideLines && (
        <div
          className='tw-absolute -tw-z-10 tw-top-0 -tw-inset-x-px tw-h-8'
          style={{ backgroundColor: "var(--marker-background-color)" }}
        />
      )}
      <div
        className={clsx(
          "tw-relative -tw-mx-px tw-overflow-hidden tw-border-0.5 tw-transition-[border-radius] tw-duration-300",
          backgroundColor,
          borderRadius,
          borderColor,
          height,
          padding,
          className,
        )}
      >
        {showLimitReachedOverlay && (
          <div className='tw-absolute tw-inset-0 tw-z-10 tw-flex tw-items-center tw-justify-center tw-bg-cream'>
            <LimitReachedOverlay
              variant={limitReachedConfig?.variant ?? "redesigned"}
              title={limitReachedConfig?.title}
              description={limitReachedConfig?.description}
              buttonText={limitReachedConfig?.buttonText}
              ctaLink={limitReachedConfig?.ctaLink}
              trackingConfig={limitReachedConfig?.trackingConfig}
              onClose={onCloseLimitReachedOverlay}
            />
          </div>
        )}
        {children}
      </div>
      {footer}
    </div>
  );
}
